/** @jsxImportSource @emotion/react */
import {
  Button,
  createStyles,
  Icon,
  Typography,
} from '@quickbit/qb-design-system';
import { useState } from 'react';

export const ApiKeyModal = ({
  onClose,
  apiKey,
}: {
  onClose: () => void;
  apiKey: string;
}) => {
  const styles = useStyles();
  const [copied, setCopied] = useState(false);

  const onHandleCopy = async () => {
    await navigator.clipboard.writeText(apiKey);
    setCopied(true);
  };

  if (copied) {
    return (
      <div css={styles.blurBox}>
        <Typography variant="subtitle">
          You have now copied it to the clipboard. Please save it in a secure
          place.
        </Typography>
        <div css={styles.buttonContainer}>
          <Button
            css={{ flex: 1 }}
            text="Show again"
            variant="text"
            onClick={() => {
              setCopied(false);
            }}
          />
          <Button
            text="I have saved my API key"
            css={{ whiteSpace: 'nowrap', flex: 2 }}
            variant="primary"
            onClick={onClose}
          />
        </div>
      </div>
    );
  }

  return (
    <div css={styles.container}>
      <div css={styles.header}>
        <Typography variant="subtitle">Your API Key</Typography>
      </div>
      <Typography variant="bodySmallBold">
        Make sure to store this key. It will disappear after you have copied it!
      </Typography>
      <div css={styles.apiKeyContainer}>
        <div css={styles.truncate}>{apiKey}</div>
        <div css={styles.action} onClick={onHandleCopy}>
          <Icon name="copy" />
        </div>
      </div>
    </div>
  );
};

const useStyles = createStyles(({ palette, spacing, sizing }) => ({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: palette.white,
    padding: spacing.s,
  },
  apiKeyContainer: {
    marginTop: spacing.xxs,
    display: 'flex',
  },
  header: {
    marginBottom: spacing.xxs,
  },
  action: {
    '&:hover': {
      cursor: 'pointer',
    },
    ':active': {
      transform: 'scale(0.9)',
    },
    svg: {
      color: palette.primary.default,
    },
  },
  truncate: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: sizing.medium,
    marginRight: spacing.xxs,
  },
  blurBox: {
    backgroundColor: palette.white,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xs,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.xxxs,
    marginTop: spacing.xs,
    width: '100%',
  },
}));
